import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  consumeGoogleAuthErrorFromHash,
  consumeGoogleCredentialFromHash,
} from '../utils/googleAuthUtils';

/**
 * Completes customer Google sign-in from OAuth redirect hash or local GIS callback.
 */
export default function useCustomerGoogleAuth({ onSuccess, onError } = {}) {
  const { loginWithGoogle } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);

  const completeGoogleSignIn = useCallback(async (credential) => {
    if (!credential) {
      return false;
    }

    setGoogleLoading(true);

    try {
      const success = await loginWithGoogle(credential);
      if (success) {
        onSuccess?.();
        return true;
      }

      onError?.();
      return false;
    } catch (error) {
      console.error('Google sign-in error:', error);
      onError?.();
      return false;
    } finally {
      setGoogleLoading(false);
    }
  }, [loginWithGoogle, onError, onSuccess]);

  useEffect(() => {
    const authError = consumeGoogleAuthErrorFromHash();
    if (authError) {
      onError?.();
      return;
    }

    const credential = consumeGoogleCredentialFromHash();
    if (credential) {
      completeGoogleSignIn(credential);
    }
  }, [completeGoogleSignIn, onError]);

  return {
    completeGoogleSignIn,
    googleLoading,
  };
};
